import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { Client, UserRepository } from "@amityco/ts-sdk";
import { Dialog, ExpanderListDriver, Paginator, Progress, Title } from "@components";
import { Button, NotFoundState, TableSearch } from "@rio-cloud/rio-uikit";
import Spinner from "@rio-cloud/rio-uikit/Spinner";
import {
  setDriversSelected,
  useAppSelector
} from "@store";
import { Driver, ISelectedDriver, NavigateState } from "@types";

import { Notification } from "@rio-cloud/rio-uikit";

import { sortDrivers, getDnGroupIdFromUrl, getDnGroupNameFromUrl } from "@utils";
import { ProgressOptions } from "src/components/Progress/Trip";
import { AuthContext } from "src/providers/AuthProvider";
import { fetchDriverListV1 } from "src/services/routes";
import { getFollowers } from "src/services/social";

const PAGE_SIZE = 10;

const TripsDriverScreen = () => {
  const dispatch = useDispatch();

  const { amityRioDrivers, all } = useContext(AuthContext)

  const { driversSelected } = useAppSelector((state) => state.trips);
  const userIdAmity = useAppSelector((state) => state.login.userIdAmity);
  const [search] = useSearchParams();
  const location = useLocation();

  const [error, setError] = useState("");

  const [searchValue, setSearchValue] = useState("");
  const [selectedItems, setSelectedItems] = useState<ISelectedDriver[]>([]);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<Driver[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pages, setPages] = useState(1);

  const [userInfo, setUserInfo] = useState<Driver | undefined>();
  const [openPerfilModal, setPerfilModal] = useState(false);
  const [openVehicleModal, setVehicleModal] = useState(false);

  const [total, setTotal] = useState(0);
  const [allDrivers, setAllDrivers] = useState<Driver[]>([]);
  const [copyfiltered, setCopyFiltered] = useState<Driver[]>([]);

  const navigateState = location.state as NavigateState | null;
  const [userDetailAmity, setUserDetailAmity] = useState<Amity.User>();

  const dnGroupName = getDnGroupNameFromUrl();
  const didAutoSelectAll = useRef(false);

  useEffect(() => {
    if (selectedItems.length === 0 && driversSelected.length > 0) {
      setSelectedItems(driversSelected);
    }
  }, [driversSelected])

  // Function to normalize text by removing accents and converting to lowercase
  const normalizeText = (text: string): string => {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  };

  const handleSearchName = (text: string): void => {
    setSearchValue(text);
    if (!text.length || text.length < 1) {
      setPage(1);
      setData(allDrivers.slice(0, PAGE_SIZE));
      setTotal(allDrivers.length);
      setPages(Math.ceil(allDrivers.length / PAGE_SIZE));
      return;
    }

    const normalizedSearchText = normalizeText(text);
    const filtered = allDrivers.filter((driver) => {
      const normalizedDriverName = normalizeText(driver?.name || '');
      return normalizedDriverName.includes(normalizedSearchText);
    });
    const firstSelectedId = selectedItems.length > 0 ? selectedItems[0].id : undefined;
    const ordered = sortDrivers(filtered, firstSelectedId);

    setTotal(ordered.length);
    setCopyFiltered(ordered);
    setPage(1);
    setData(ordered.slice(0, PAGE_SIZE));
    setPages(Math.ceil(ordered.length / PAGE_SIZE));
  };

  const initialData = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const [response, resFollowers] = await Promise.all([
        fetchDriverListV1(),
        getFollowers(userIdAmity),
      ]);


      const rioUsers: Driver[] = response.data.result.drivers.map((driver: Driver): Driver => {
        const id = driver.uniqueProfileId ? driver.uniqueProfileId : driver.userId;

        if (!id) {
          return { ...driver, isEnabled: false, type: 'MY_TEAM', origin: 'RIO' };
        }

        const metadataRioUser = amityRioDrivers?.[id]?.metadata || {};

        const name = metadataRioUser?.displayName
          ? metadataRioUser.displayName
          : metadataRioUser?.givenName && metadataRioUser?.familyName
            ? metadataRioUser.givenName + " " + metadataRioUser.familyName
            : driver.name;

        return {
          ...driver,
          userId: driver?.userId,
          uniqueProfileId: driver.uniqueProfileId,
          name: name,
          avatar: metadataRioUser?.imageProfile?.fileUrl || "",
          type: driver.profileType === "MANAGER" ? "MANAGER" : "MY_TEAM",
          origin: "RIO",
          isEnabled: !!amityRioDrivers?.[id]?.metadata,
        };
      });


      const amityUsers: Driver[] = resFollowers.data?.result?.users
        ?.filter((driver: Amity.User) =>
          (driver?.metadata?.profileType && driver?.metadata?.profileType !== "MANAGER") ||
          (driver?.metadata?.profileType === "MANAGER" && driver?.metadata?.cnhCategory)
        )
        .filter((driver: Amity.User) => !rioUsers?.some((userRio: Driver) => userRio.uniqueProfileId === driver.userId || userRio.userId === driver.userId))
        .map((user: Amity.User): Driver => {
          const metadata = user?.metadata;

          return {
            id: user.userId,
            name: metadata?.displayName || metadata?.givenName + " " + metadata?.familyName,
            userId: user.userId,
            avatar: metadata?.imageProfile?.fileUrl,
            uniqueProfileId: user.userId,
            type: metadata?.profileType === 'MANAGER' ? 'MANAGER' : "CONNECTED",
            origin: "AMITY",
            isEnabled: true,
          };
        });

      const dnGroupId = getDnGroupIdFromUrl();
      const isEditTrip = !!search.get('editTrip');
      const acceptedDriverId = search.get('accepted-driver-id');
      const grouped: Driver[] = dnGroupId ? [...rioUsers] : [...amityUsers, ...rioUsers];
      const scoped: Driver[] = dnGroupId && isEditTrip && acceptedDriverId
        ? grouped.filter((d) =>
          d.userId === acceptedDriverId ||
          d.id === acceptedDriverId ||
          d.uniqueProfileId === acceptedDriverId)
        : grouped;
      const totalArr: number = scoped.length;

      const firstSelected = driversSelected.length > 0 ? driversSelected[0] : null;
      const selectedId = dnGroupId && isEditTrip
        ? undefined
        : firstSelected
          ? scoped.find((d) => d.userId === firstSelected.id || d.id === firstSelected.id)?.id
          : undefined;
      const finalDrivers: Driver[] = sortDrivers(scoped, selectedId);

      setAllDrivers(finalDrivers);
      setPage(1);
      setData(finalDrivers.slice(0, PAGE_SIZE));
      setTotal(totalArr);
      setPages(Math.ceil(totalArr / PAGE_SIZE));

      if (dnGroupId && !isEditTrip && !didAutoSelectAll.current && finalDrivers.length > 0) {
        didAutoSelectAll.current = true;
        const allSelected: ISelectedDriver[] = finalDrivers.map((driver) => ({
          id: driver.userId || driver.id || "",
          uniqueId: driver.uniqueProfileId || "",
          name: driver.name || "",
          avatar: driver.avatar || "",
        }));
        setSelectedItems(allSelected);
        dispatch(setDriversSelected(allSelected));
      }
    } catch (error) {
      console.error(error);

      setAllDrivers([]);
      setData([]);
      setTotal(0);
      setPages(1);
    } finally {
      setIsLoading(false);
    }
  };

  const modals = {
    CALENDAR: (driver: Driver) => {
      console.log(`open calendar modal ${driver.id}`);
    },
    PROFILE: (driver: Driver) => handlePerfilSocial(driver),
    VEHICLE: (driver: Driver) => {
      console.log(`vehicle modal open ${driver.id}`);
    },
  };

  const handlePerfilSocial = async (driver: Driver) => {
    try {
      setUserInfo(driver);
      const userDriverId = driver.uniqueProfileId ? driver.uniqueProfileId : driver.userId;
      const response = await UserRepository.getUserByIds([userDriverId!]);
      setUserDetailAmity(response?.data ? response.data[0] : undefined);

      setPerfilModal(true);
    } catch (error) {
      console.error(error)
    }

  };

  const handleClosePerfilModal = () => {
    setPerfilModal(false);
  };

  const handleCloseVehicleModal = () => {
    setVehicleModal(false);
  };

  const handleChecked = (
    checked: boolean,
    id: string,
    name: string,
    _userIdN?: string,
    avatar?: string,
    uniqueProfileIdN?: string,
  ): void => {
    const driverUserId = _userIdN || id;
    const alreadySelected = selectedItems.some(d => d.id === driverUserId);

    if (alreadySelected) {
      setSelectedItems(prev => prev.filter(d => d.id !== driverUserId));
      return;
    }

    const newDriver: ISelectedDriver = {
      id: driverUserId,
      uniqueId: uniqueProfileIdN || '',
      name: name,
      avatar: avatar || '',
    };

    setSelectedItems(prev => [...prev, newDriver]);
  };

  const navigate = useNavigate();
  const currentParams = new URLSearchParams(search);

  const isUptimeEmergency = !!search?.get('dn-group-id');

  const saveDrivers = () => {
    const enriched = selectedItems.map(driver => {
      if (!driver.uniqueId) {
        const find = all?.find((res: { userId: string }) => res?.userId === driver.id);
        return { ...driver, uniqueId: find?.uniqueProfileId || '' };
      }
      return driver;
    });
    dispatch(setDriversSelected(enriched));
  }

  const handleSubmit = () => {
    saveDrivers();

    const nextRoute = isUptimeEmergency
      ? (currentParams.get('editTrip') ? '/routing/trips/update?' : '/routing/trips/custom?')
      : '/routing/trips/vehicle?';
    navigate(nextRoute + currentParams.toString(), {
      state: {
        edit: navigateState?.edit,
        csv: navigateState?.csv,
        csvData: navigateState?.csvData,
      }
    })
  };

  const handleCancel = () => {
    const url = currentParams.get('editTrip') ? '/routing/trips/update?' : '/routing/trips/schedule?';
    navigate(url + currentParams.toString(), {
      state: {
        edit: navigateState?.edit,
        csv: navigateState?.csv,
        csvData: navigateState?.csvData,
      }
    })
  };

  const ManipulatorPage = (list: Driver[], newPage: number, _selectedDriverId?: string, isNavigation: boolean = true) => {
    setPage(newPage);

    const startIndex = (newPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;

    if (newPage > 1 && isNavigation) {
      setData(prevData => [...prevData, ...list.slice(startIndex, endIndex)]);
    } else {
      setData(list.slice(startIndex, endIndex));
    }
  }

  const handlePage = (params: { page: number, pages?: number }) => {
    let newPage = params.page || page;
    newPage = newPage === 0 ? 1 : newPage;
    let list = searchValue && searchValue !== '' ? copyfiltered : allDrivers;

    ManipulatorPage(list, newPage, undefined, true);
  };

  useEffect(() => {
    if (Client?.getActiveClient().token?.accessToken) {
      initialData();
    }
  }, [Client?.getActiveClient().token?.accessToken, amityRioDrivers]);

  useEffect(() => {
    if (error) {
      Notification.error(error);
    }
  }, [error])

  const [content, setContent] = useState(5);

  const selectedIds = selectedItems.map(d => d.id);

  const pageIndex = 1;
  const onContent = (idx: number) => {
    if (selectedItems.length === 0 && idx > pageIndex) {
      setError("Selecione ao menos um motorista")
      return;
    }

    if (selectedItems.length > 0)
      saveDrivers()

    const resolvedIdx = isUptimeEmergency && idx >= 2 ? 3 : idx;
    navigate(ProgressOptions[resolvedIdx]?.route + '?' + currentParams.toString(), { state: { edit: navigateState?.edit, cvs: navigateState?.csv } })
  }

  return (
    <div className="display-flex flex-column gap-20 padding-y-20 padding-x-50">
      <div>
        <Title
          icon="rioglyph rioglyph-user"
          title="Motorista para a viagem"
        />
      </div>
      <div>
        <Progress.Trip
          selected={content}
          onSelectedChanged={onContent}
          disabledIndex={pageIndex + 1}
          step={pageIndex}
          hideVehicle={isUptimeEmergency}
        />
      </div>
      <div>
        <TableSearch value={searchValue} onChange={handleSearchName} placeholder="Buscar motorista" />
      </div>
      <div className="padding-25 bg-white rounded shadow-default">
        <div className="margin-bottom-15 display-flex justify-content-between align-items-center">
          <span className="text-bold text-size-16">
            {total} motoristas encontrados:
            {dnGroupName && <span className="text-color-black margin-left-5">{dnGroupName}</span>}
          </span>
          {selectedItems.length > 0 && (
            <span className="badge badge-primary text-size-14">
              {selectedItems.length} motorista{selectedItems.length > 1 ? 's' : ''} selecionado{selectedItems.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        {
          isLoading ? (
            <div className="display-flex justify-content-center align-items-center padding-25">
              <Spinner />
            </div>
          ) :
            data && data.length > 0 ? (
              <ExpanderListDriver
                onChecked={handleChecked}
                selected={selectedIds}
                onClickButton={(type, _, driver) => {
                  modals[type](driver!);
                }}
                data={data}
              />
            ) : (
              <NotFoundState
                headline="Nenhum motorista disponível"
                message="Volte e tente novamente."
                fullWidth
                outerClassName="border-none"
              />
            )}

        <div className="display-flex justify-content-between align-items-center">
          <div className="display-flex width-200">
          </div>
          <div className="display-flex justify-content-center">
            <Paginator
              handlePage={params => handlePage(params)}
              pages={pages}
              page={page}
              pageSize={PAGE_SIZE}
              disabled={data.length == 0}
              hideNoMoreMessage={!!getDnGroupIdFromUrl()}
            />
          </div>
          <div className="display-flex gap-10">
            <Button bsSize={Button.MD} className="width-84" onClick={handleCancel}>
              Voltar
            </Button>
            <Button disabled={selectedItems.length === 0} bsStyle={Button.PRIMARY} bsSize={Button.MD} className="width-157" onClick={handleSubmit} >
              {navigateState?.edit ? "Aplicar" : 'Avançar'}
            </Button>
          </div>
        </div>
        <Dialog.PerfilSocial driver={userInfo} open={openPerfilModal} onClose={handleClosePerfilModal} userProfile={userDetailAmity} />
        <Dialog.DriverVehicle id={userInfo?.id} open={openVehicleModal} onClose={handleCloseVehicleModal} />
      </div>
    </div>
  );
};

export default TripsDriverScreen;

